<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="gl-master-main">
    <div>
      <Menus :menu="menu" @menuClicked="handleMenuClick" />
    </div>
    <div class="table-box-container">
      <div class="dropdown-container">
        <Dropdown
          :items="financialYear"
          placeholder="Financial Year"
          itemKey="Fyear"
          :selectedObject.sync="selectedYear"
          @selected="getReportTypeData"
        />
        <Dropdown
          :items="reportType"
          style="margin-left: 20px"
          placeholder="Financial Statement"
          itemKey="Report"
          :selectedObject.sync="selectedReportType"
          @selected="getReportTypeData"
        />
      </div>
      <TableBox
        title="Profit & Loss Account"
        :tableData="pnlMasterListMaster"
        :maxSelection="1"
        :selectedRow.sync="pnlMasterTableBoxSelectedRow"
        :loading="pnlTableBoxLoading"
        style="max-height: 95%"
        v-if="showPNLTable"
        :hideColumns="[
          'PNLSeq',
          'PK-FORWARDING',
          'PG-HAULAGE',
          'PK-HAULAGE',
          'PK-FREIGHT',
          'TOTAL',
          'INTERNAL',
          'CONSOL',
          'Record Type',
        ]"
        :columnWidth="['150', '500', '150']"
      />
      <TableBox
        title="Balance Sheet Account"
        :tableData="bsMasterListMaster"
        :maxSelection="1"
        :selectedRow.sync="bsMasterTableBoxSelectedRow"
        :loading="bsTableBoxLoading"
        style="max-height: 95%"
        v-if="showBSTable"
        :hideColumns="[
          'BSSeq',
          'PK-FORWARDING',
          'PG-HAULAGE',
          'PK-HAULAGE',
          'PK-FREIGHT',
          'TOTAL',
          'INTERNAL',
          'CONSOL',
          'Record Type',
        ]"
        :columnWidth="['150', '500', '150']"
      />
    </div>

    <PopupEntry
      class="popup-entry-component"
      :show.sync="showPopupEntry"
      v-if="showPopupEntry"
      :title="popupEntryTitle"
      @save="popupEntrySaveClicked"
      :requiredError="popupEntryRequiredError"
      :successMessages="popupEntrySuccessMessages"
      :messages="popupEntryMessages"
      :errorMessages="popupEntryErrorMessages"
      @requiredClicked="popupEntryRequiredClicked"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
    >
      <!-- GL Account Tab -->
      <div v-if="showGLAccount" class="gl-account-general-input">
        <div class="input-container">
          <div class="input-left-container">
            <Input
              placeholder="GL Account Code"
              :inputValue.sync="glAccountCodeInputValue"
              :isRequired="true"
              :errorMessage.sync="glAccountCodeInputValueErr"
              :isDisabled="viewModePK"
              validator="masterfilecode,20,uppercase"
              ref="glAccountCodeInputValueErr"
            />
            <Input
              placeholder="GL Account Description"
              :inputValue.sync="glAccountDescriptionInputValue"
              :isRequired="true"
              :errorMessage.sync="glAccountDescriptionInputValueErr"
              :isDisabled="viewMode"
              validator="text,100"
              ref="glAccountDescriptionInputValueErr"
            />
            <DateTimePicker
              :isRequired="true"
              placeholder="Opening Date"
              :showTime="false"
              :value.sync="glAccountOpeningDateValue"
              :errorMessage.sync="glAccountOpeningDateValueErr"
              :isDisabled="viewMode"
            />
            <Dropdown
              :items="accIndList"
              placeholder="Account Type"
              :selectedObject.sync="selectedAccIndValue"
              :errorMessage.sync="selectedAccIndErr"
              :isRequired="true"
              validator="text,50"
              :isDisabled="viewMode"
              itemKey="AccIndDesc"
              v-if="selectedReportType.Report == 'Profit & Loss'"
              ref="selectedAccIndErr"
            />
            <Dropdown
              :items="glCostDeptList"
              placeholder="GL Cost Dept"
              :selectedObject.sync="selectedGLCostDeptListValue"
              :errorMessage.sync="selectedGLCostDeptListErr"
              :isRequired="true"
              validator="text,50"
              :isDisabled="viewMode"
              itemKey="DepartmentName"
              v-if="selectedReportType.Report == 'Profit & Loss'"
              ref="selectedGLCostDeptListErr"
            />
            <Dropdown
              :items="transactionTypeList"
              placeholder="Transaction Type "
              itemKey="TxnTypeDesc"
              :selectedObject.sync="selectedGLTransactionType"
              :isDisabled="viewMode"
              :isRequired="true"
              :errorMessage.sync="selectedGLTransactionTypeErr"
              ref="selectedGLTransactionTypeErr"
              @selected="setDefaultBalanceType"
            />
            <InputQuery
              placeholder="Default Tax Code"
              :isDisabled="viewMode"
              :items="defaultTaxCodeList"
              :allowFreeText="false"
              :selectedObject.sync="selectedDefaultTaxCode"
              itemKey="taxCodeText"
              validator="text,108"
            />
            <InputQuery
              placeholder="Default Tax GL Acc Code"
              :isDisabled="viewMode"
              :items="glAccCodeList"
              :allowFreeText="false"
              :selectedObject.sync="selectedDefaultTaxGLAccCode"
              itemKey="accCodeText"
              validator="text,108"
            />
            <InputQuery
              placeholder="Gain GL Acc Code"
              :isDisabled="viewMode"
              :items="glAccCodeList"
              :allowFreeText="false"
              :selectedObject.sync="selectedGainGLAccCode"
              itemKey="accCodeText"
              validator="text,108"
            />
            <InputQuery
              placeholder="Loss GL Acc Code"
              :isDisabled="viewMode"
              :items="glAccCodeList"
              :allowFreeText="false"
              :selectedObject.sync="selectedLossGLAccCode"
              itemKey="accCodeText"
              validator="text,108"
            />
            <InputQuery
              placeholder="Classification"
              :isDisabled="viewMode"
              :items="GLMasterClassListMaster"
              :allowFreeText="false"
              :selectedObject.sync="selectedClassification"
              itemKey="Classification Code"
              descKey="Description"
            />
            <InputQuery
              placeholder="GST Major Industry Code"
              :items="MSICData"
              itemKey="MSIC Code"
              descKey="Description"
              :selectedObject.sync="selectedMSICCode"
              :isDisabled="viewMode"
              :allowFreeText="false"
            />
            <Dropdown
              :items="groupUnderList"
              placeholder="Group Under"
              :isRequired="true"
              itemKey="Group Description"
              :selectedObject.sync="selectedGroupUnder"
              :isDisabled="viewMode"
              :errorMessage.sync="selectedGroupUnderErr"
              ref="selectedGroupUnderErr"
            />
            <InputQuery
              placeholder="Sub Account Of"
              :isRequired="false"
              :isDisabled="viewMode"
              :items="glAccCodeList"
              :allowFreeText="false"
              :selectedObject.sync="selectedSubAccount"
              itemKey="accCodeText"
              validator="text,108"
              :errorMessage.sync="selectedSubAccountErr"
              ref="selectedSubAccountErr"
            />
            <InputQuery
              placeholder="Fixed Assets (Net Book Value)"
              :isRequired="false"
              :isDisabled="viewMode"
              :items="glAccCodeList"
              :allowFreeText="false"
              :selectedObject.sync="selectedFixedAsset"
              itemKey="accCodeText"
              validator="text,108"
              :errorMessage.sync="selectedFixedAssetErr"
              ref="selectedFixedAssetErr"
            />
            <Dropdown
              placeholder="Balance Type"
              :isRequired="true"
              :items="balanceTypeList"
              itemKey="key"
              :selectedObject.sync="selectedBalanceType"
              :errorMessage.sync="selectedBalanceTypeErr"
              :isDisabled="viewMode"
            />
            <Input
              placeholder="Opening Balance"
              :inputValue.sync="openingBalanceValue"
              validator="positiveCurrency,11"
              :isDisabled="viewMode"
            />
            <Input placeholder="Balance C/F" :inputValue.sync="balanceCFValue" :isDisabled="true" validator="text,50" />
          </div>
          <div class="input-center-container">
            <div class="ap-ar-table-box-container">
              <div v-if="!viewMode" class="add-delete-buttons">
                <button
                  @click="addARChargeCodeButtonClicked"
                  :disabled="showARChargeCodeInput"
                  style="margin-right: 0.625rem"
                  >Add</button
                >
                <button @click="deleteARChargeCodeButtonClicked" :disabled="showARChargeCodeInput">Delete</button>
              </div>
              <div class="ap-ar-tablebox">
                <TableBox
                  title="AR Charge Code List"
                  :tableData="arChargeCodeListWithoutDeleted"
                  :selectedRow.sync="selectedARChargeCodeTableRow"
                  :hideBottomBlock="true"
                  v-if="showARChargeCodeTable"
                  :hideColumns="arChargeCodeTableBoxHideCols"
                />
              </div>
              <div v-if="!viewMode" class="add-delete-buttons">
                <button
                  @click="addAPChargeCodeButtonClicked"
                  :disabled="showAPChargeCodeInput"
                  style="margin-right: 0.625rem"
                  >Add</button
                >
                <button @click="deleteAPChargeCodeButtonClicked" :disabled="showAPChargeCodeInput">Delete</button>
              </div>
              <div class="ap-ar-tablebox">
                <TableBox
                  title="AP Charge Code List"
                  :tableData="apChargeCodeListWithoutDeleted"
                  :selectedRow.sync="selectedAPChargeCodeTableRow"
                  :hideBottomBlock="true"
                  v-if="showAPChargeCodeTable"
                  :hideColumns="apChargeCodeTableBoxHideCols"
                />
              </div>
            </div>
          </div>
          <div class="input-right-container" v-if="showARChargeCodeInput || showAPChargeCodeInput">
            <div v-if="!viewMode && showARChargeCodeInput" class="add-delete-buttons">
              <button @click="addARChargeCode" style="margin-right: 0.625rem">Add</button>
              <button @click="cancelARChargeCodeButtonClicked">Cancel</button>
            </div>
            <div class="charge-code-input-selection">
              <TableBox
                title="AR Charge Code List"
                :tableData="chargeCodeList"
                :maxSelection="-1"
                :selectedRow.sync="selectedARChargeCode"
                :loading="chargeCodeTableBoxLoading"
                v-if="showARChargeCodeInput"
              />
            </div>
            <div v-if="!viewMode && showAPChargeCodeInput" class="add-delete-buttons">
              <button @click="addAPChargeCode" style="margin-right: 0.625rem">Add</button>
              <button @click="cancelAPChargeCodeButtonClicked">Cancel</button>
            </div>
            <div class="charge-code-input-selection">
              <TableBox
                title="AP Charge Code List"
                :tableData="chargeCodeList"
                :maxSelection="-1"
                :selectedRow.sync="selectedAPChargeCode"
                :loading="chargeCodeTableBoxLoading"
                v-if="showAPChargeCodeInput"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Group Settings -->
      <div
        style="display: flex; flex-direction: row; height: 100%; width: 100%"
        v-if="!showGLAccount && previewGroupTableData && previewGroupTableData.length > 0 && !viewMode"
      >
        <div class="group-main-body" style="display: flex; flex-direction: column; overflow-y: auto; flex: 1">
          <div class="group-add-del-buttons" style="display: flex; justify-content: flex-end">
            <button :disabled="showGroupNewEdit" @click="showGroupNewEdit = !showGroupNewEdit">Add</button>
            <button :disabled="showGroupNewEdit" @click="deleteGroupClick" style="margin-left: 5px">Delete</button>
          </div>
          <div class="group-table" style="display: flex; height: calc(100% - 28px)">
            <TableBox
              title="Preview of Groups"
              :tableData="previewGroupTableData"
              :selectedRow.sync="selectedSubgroupsTableBoxRow"
              :hideColumns="['Date Updated', 'TxnType', 'Seq']"
              :maxSelection="1"
              :hideBottomBlock="true"
              :columnWidth="previewGroupTableDataColumnWidth"
              :additionalButtons="[{ text: 'Edit', index: 0, width: 60 }]"
              @buttonClicked="editPreviewGroupClick"
              v-if="showPreviewTable && menuNewButtonClicked"
            />
            <div class="reorder-buttons" v-if="!viewMode && !showGroupNewEdit">
              <button @click="previewMoveUpButtonClicked">
                <img src="https://dsmlib.logistics.systems/Icons/icons8-up-arrow-26.png" />
              </button>
              <button @click="previewMoveDownButtonClicked">
                <img src="https://dsmlib.logistics.systems/Icons/icons8-down-arrow-26.png" />
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="showGroupNewEdit"
          class="group-new-edit"
          style="width: 40%; margin-left: 5px; border-left: 1px solid black"
        >
          <div style="display: flex; justify-content: flex-end">
            <button v-if="!editGroup" @click="addGroupClick">Add</button>
            <button v-if="editGroup" @click="editGroupClick">Edit</button>
            <button @click="addEditCancelClick" style="margin-left: 5px">Cancel</button>
          </div>
          <Dropdown
            :items="computedCategoryList"
            placeholder="Select Type"
            itemKey="desc"
            :selectedObject.sync="selectedCategory"
            :errorMessage.sync="categoryErr"
            :isRequired="true"
            :isDisabled="editGroup"
            @selected="handleCategoryChange"
            v-if="menuNewButtonClicked && !viewMode"
            ref="categoryErr"
          />

          <!-- Group Tab-->
          <div v-if="showGroup" class="group-input" style="display: flex; flex-direction: column">
            <Input
              placeholder="Group Code"
              :inputValue.sync="groupCodeInputValue"
              :isRequired="true"
              :errorMessage.sync="groupCodeErr"
              :isDisabled="viewModePK || editGroup"
              validator="positiveInteger,4"
              ref="groupCodeErr"
            />
            <Input
              placeholder="Group Title in Financial Statement"
              :inputValue.sync="groupTitleInputValue"
              :isRequired="true"
              :errorMessage.sync="groupTitleErr"
              :isDisabled="viewMode"
              validator="text,50"
              ref="groupTitleErr"
            />
            <!-- Group Title will also be used to insert into settingsVehPNL's title, and that column only takes 50 chars -->
            <Input
              placeholder="Group Description"
              :inputValue.sync="groupDescription"
              :isRequired="true"
              :errorMessage.sync="groupDescriptionErr"
              :isDisabled="viewMode"
              validator="text,50"
              ref="groupDescriptionErr"
            />
            <!-- Group description will also be used to insert into settingsVehPNL's title, and that column only takes 50 chars -->
            <Dropdown
              :items="transactionTypeList"
              :isRequired="true"
              placeholder="Transaction Type"
              itemKey="TxnTypeDesc"
              :selectedObject.sync="selectedTransactionType"
              :errorMessage.sync="groupTransactionTypeErr"
              :isDisabled="viewMode"
              ref="groupTransactionTypeErr"
            />
            <div style="display: flex; gap: 1em" v-if="subgroupsNameList && subgroupsNameList.length > 0">
              <Dropdown
                :items="beforeAfterDropdownList"
                placeholder="Add Before / After"
                itemKey="BAdesc"
                :selectedObject.sync="beforeAfterDropdownValue"
                :errorMessage.sync="beforeAfterDropdownValueErr"
                v-if="!viewMode && menuNewButtonClicked"
                :isDisabled="editGroup"
                style="flex: 0.2"
                ref="beforeAfterDropdownValueErr"
              />
              <Dropdown
                :items="groupTitleAddList"
                placeholder="Group Title"
                :selectedObject.sync="existingGroupListValue"
                :errorMessage.sync="existingGroupListValueErr"
                itemKey="Group Title in Financial Statement"
                v-if="!viewMode && menuNewButtonClicked"
                :isDisabled="editGroup"
                style="flex: 0.8"
                ref="existingGroupListValueErr"
              />
              <button :disabled="editGroup" @click="groupAddClicked">Preview</button>
            </div>
          </div>

          <!-- Subtotal Tab-->
          <div v-if="showSubtotal" style="display: flex; flex-direction: column">
            <Input
              placeholder="Description"
              :inputValue.sync="subtotalDescription"
              :isDisabled="viewMode"
              :isRequired="true"
              validator="text,50"
              ref="subtotalDescription"
              :errorMessage.sync="subtotalDescriptionErr"
            />
            <Input
              placeholder="Operation"
              :inputValue.sync="subtotalOperation"
              :isDisabled="viewMode"
              :isRequired="true"
              :regex="{ regex: /[0-9+\-]/g, live: true }"
              ref="subtotalOperation"
              :errorMessage.sync="subtotalOperationErr"
            />
            <div
              style="display: flex; gap: 1em; margin-top: 5px"
              v-if="subgroupsNameList && subgroupsNameList.length > 0"
            >
              <Dropdown
                :items="beforeAfterDropdownList"
                placeholder="Add Before / After"
                itemKey="BAdesc"
                :selectedObject.sync="beforeAfterDropdownValue"
                :errorMessage.sync="beforeAfterDropdownValueErr"
                :isDisabled="editGroup"
                v-if="!viewMode && menuNewButtonClicked"
                style="flex: 0.2"
                ref="beforeAfterDropdownValueErr"
              />
              <Dropdown
                :items="subgroupsNameList"
                placeholder="Group Title"
                :selectedObject.sync="existingGroupListValue"
                :errorMessage.sync="existingGroupListValueErr"
                itemKey="Group Title in Financial Statement"
                :isDisabled="editGroup"
                v-if="!viewMode && menuNewButtonClicked"
                style="flex: 0.8"
                ref="existingGroupListValueErr"
              />
              <button :disabled="editGroup" @click="groupAddClicked">Preview</button>
            </div>
          </div>
        </div>
      </div>
    </PopupEntry>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    computedCategoryList() {
      if (!this.showGLAccount) {
        return this.categoryList.filter((f) => f['key'].toUpperCase() != 'GLACCOUNT');
      }
      return this.categoryList.filter((f) => f['key'].toUpperCase() == 'GLACCOUNT');
    },
    showPNLTable() {
      return this.selectedReportType?.Report === 'Profit & Loss' && this.selectedYear;
    },
    showBSTable() {
      return this.selectedReportType?.Report === 'Balance Sheet' && this.selectedYear;
    },
    arChargeCodeListWithoutDeleted: function () {
      return (this.selectedARChargeCodeList || []).filter((arChargeCodeData) => arChargeCodeData._action !== 'D');
    },
    apChargeCodeListWithoutDeleted: function () {
      return (this.selectedAPChargeCodeList || []).filter((apChargeCodeData) => apChargeCodeData._action !== 'D');
    },
    groupTitleAddList() {
      //return the list with subgroup or operation with arithmetic only because each subgroup also have a operation that should not be used for the dropdown
      return this.subgroupsNameList.filter(f => f['Record Type'] == 'Subgroup' || this.hasArithmeticOperation(f['Operation']));
    }
  },
  data: function () {
    return {
      menu: [
        {
          title: 'New',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          key: 'MnuNew',
          visible: true,
          enabled: false,
        },
        {
          title: 'Edit',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png',
          key: 'MnuEdit',
          visible: true,
          enabled: false,
        },
        {
          title: 'Delete',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png',
          key: 'MnuDelete',
          visible: false,
          enabled: false,
        },
        {
          title: 'View',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png',
          key: 'MnuQuery',
          visible: true,
          enabled: false,
        },
        {
          title: 'Group Settings',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png',
          key: 'MnuReorder',
          visible: true,
          enabled: false,
        },
      ],

      menuNewButtonClicked: false,

      selectedCategory: null,
      showGroup: false,
      showGroupNewEdit: false,
      editGroup: false,
      showGLAccount: false,
      showSubtotal: false,
      groupChangesArray: [],

      categoryErr: '',

      categoryList: [
        { key: 'group', desc: 'Group' },
        { key: 'glAccount', desc: 'GL Account' },
        { key: 'subtotal', desc: 'Subtotal' },
      ],

      // For Table Data
      pnlMasterListMaster: [],
      pnlMasterTableBoxSelectedRow: [],
      bsMasterListMaster: [],
      bsMasterTableBoxSelectedRow: [],
      pnlTableBoxLoading: false,
      bsTableBoxLoading: false,

      // For Dropdown(s) OR InputQuery(s)
      transactionTypeList: [],
      defaultTaxCodeList: [],
      balanceTypeList: [
        { key: 'DEBIT', value: '0' },
        { key: 'CREDIT', value: '1' },
      ],
      glAccCodeList: [],
      groupUnderList: [],
      accIndList: [],
      glCostDeptList: [],
      chargeCodeList: [],
      MSICData: [],
      GLMasterClassListMaster: [],

      financialYear: [],
      reportType: [],

      selectedYear: null,
      selectedReportType: null,

      fYearStartDate: null,
      fYearEndDate: null,

      // For PopupEntry State
      popupEntryTitle: '',
      showPopupEntry: false,
      viewMode: false,
      viewModePK: false,
      popupEntryRequiredError: [],
      popupEntrySuccessMessages: [],
      popupEntryMessages: [],
      popupEntryErrorMessages: [],

      // For PopupEntry Fields

      // Group Fields
      beforeAfterDropdownList: [
        { key: 'B', BAdesc: 'Before' },
        { key: 'A', BAdesc: 'After' },
      ],

      groupCodeInputValue: '',
      groupTitleInputValue: '',
      groupDescription: '',
      selectedTransactionType: null,
      beforeAfterDropdownValue: null,
      existingGroupListValue: null,
      previewTableLoading: true,
      showPreviewTable: true,
      previewGroupTableData: [],
      previewGroupTableDataColumnWidth: [60, 120, 280, 120, 120],

      subgroupsNameList: [],
      subgroupNameListFirstDateUpdated: '',
      selectedSubgroupsTableBoxRow: [],

      groupCodeErr: '',
      groupTitleErr: '',
      groupDescriptionErr: '',
      groupTransactionTypeErr: null,
      beforeAfterDropdownValueErr: null,
      existingGroupListValueErr: null,

      // GL Account Fields
      glAccountCodeInputValue: '',
      glAccountDescriptionInputValue: '',
      glAccountOpeningDateValue: null,
      selectedGLTransactionType: null,
      selectedDefaultTaxCode: null,
      selectedDefaultTaxGLAccCode: null,
      selectedGainGLAccCode: null,
      selectedLossGLAccCode: null,
      selectedGroupUnder: null,
      selectedSubAccount: null,
      selectedFixedAsset: null,
      selectedARChargeCodeList: [],
      selectedAPChargeCodeList: [],
      selectedARChargeCodeTableRow: [],
      selectedAPChargeCodeTableRow: [],
      selectedBalanceType: null,
      openingBalanceValue: '0',
      balanceCFValue: '',
      selectedAccIndValue: null,
      selectedGLCostDeptListValue: null,

      selectedARChargeCode: [],
      showARChargeCodeTable: true,
      arChargeCodeTableBoxHideCols: ['_action'],
      selectedAPChargeCode: [],
      showAPChargeCodeTable: true,
      apChargeCodeTableBoxHideCols: ['_action'],

      chargeCodeTableBoxLoading: false,
      chargeCodeListTableBoxSelectedRow: [],

      glAccountCodeInputValueErr: '',
      glAccountDescriptionInputValueErr: '',
      glAccountOpeningDateValueErr: '',
      selectedGLTransactionTypeErr: null,
      selectedDefaultTaxGLAccCodeErr: null,
      selectedGainGLAccCodeErr: null,
      selectedLossGLAccCodeErr: null,
      selectedGroupUnderErr: null,
      selectedSubAccountErr: null,
      selectedAccIndErr: '',
      selectedGLCostDeptListErr: null,
      selectedBalanceTypeErr: '',
      selectedFixedAssetErr: null,
      selectedClassification: null,
      selectedMSICCode: null,
      arChargeCodeErr: '',
      apChargeCodeErr: '',

      showARChargeCodeInput: false,
      showAPChargeCodeInput: false,

      // Subtotal Fields
      subtotalSeq: '',
      subtotalDescription: '',
      subtotalDescriptionErr: '',
      subtotalOperation: '',
      subtotalOperationErr: '',
    };
  },
  props: {},
  methods: {
    // API Call for Year
    getYearListMaster: async function () {
      this.financialYear = [];
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GETGLMASTERFYEAR' });
      this.financialYear = result;
    },

    // API Call for Report Type
    getReportTypeListMaster: async function () {
      this.reportType = [];
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GETGLREPORT' });
      this.reportType = result;
    },

    // API Call for Report Type
    getPNLAnalysisTableData: async function (year) {
      this.pnlMasterListMaster = [];
      this.pnlTableBoxLoading = true;
      this.$fullscreenLoading(`Loading Data for ${year}...`);
      const result = await this.$apiCallV2('/GLMaster/GetPNLAnalysis', {
        rpttype: 'DETAILS_FULL',
        startdate: this.fYearStartDate ? this.fYearStartDate.formatString('yyyy-MM-dd') : '',
        enddate: this.fYearEndDate ? this.fYearEndDate.formatString('yyyy-MM-dd') : '',
        rotatetable: '1',
      });
      this.pnlMasterListMaster = result;
      this.pnlTableBoxLoading = false;
      this.$fullscreenLoading();
    },

    // API Call for BS
    getBSAnalysisTableData: async function (year) {
      this.bsMasterListMaster = [];
      this.bsTableBoxLoading = true;
      this.$fullscreenLoading(`Loading Data for ${year}...`);
      const result = await this.$apiCallV2('/GLMaster/GetBSAnalysis', {
        rpttype: 'DETAILS_FULL',
        startdate: this.fYearStartDate ? this.fYearStartDate.formatString('yyyy-MM-dd') : '',
        enddate: this.fYearEndDate ? this.fYearEndDate.formatString('yyyy-MM-dd') : '',
        rotatetable: '1',
      });
      this.bsMasterListMaster = result;
      this.bsTableBoxLoading = false;
      this.$fullscreenLoading();
    },

    // API Call for View GL Account
    getGLAccountDetails: async function (glAccountID) {
      return await this.$apiCallV2('/GLMaster/GetGLMaster', {
        accno: glAccountID,
        fyear: this.selectedYear['Fyear'],
      });
    },

    // API Call for Group Transaction Type
    getTxnType: async function () {
      this.$fullscreenLoading('Getting Data');
      this.transactionTypeList = [];
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GETGLTXNTYPE' });
      this.transactionTypeList = result;
      this.$fullscreenLoading('');
    },

    // API Call for Group Under (PNL)
    getPNLGroupUnderList: async function () {
      this.$fullscreenLoading('Getting Data');
      this.groupUnderList = [];
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GETGLSUBGROUP_P' });
      this.groupUnderList = result;
      this.$fullscreenLoading('');
    },

    // API Call for Group Under (BS)
    getBSGroupUnderList: async function () {
      this.groupUnderList = [];
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GETGLSUBGROUP_B' });
      this.groupUnderList = result;
    },

    // API Call for Subgroup Names
    getPNLSubgroupNames: async function () {
      this.previewTableLoading = true;
      this.subgroupsNameList = [];
      this.groupChangesArray = [];
      const result = (await this.$apiCallV2('/GLMaster/GetSettingsVehPNL', { rpttype: 'P' }))['order'];
      this.subgroupsNameList = result;
      if (this.subgroupsNameList.length > 0) {
        this.subgroupNameListFirstDateUpdated = this.subgroupsNameList[0]['Date Updated'];
      }
      this.previewTableLoading = false;
    },

    getBSSubgroupNames: async function () {
      this.previewTableLoading = true;
      this.subgroupsNameList = [];
      this.groupChangesArray = [];
      const result = (await this.$apiCallV2('/GLMaster/GetSettingsVehPNL', { rpttype: 'B' }))['order'];
      this.subgroupsNameList = result;
      if (this.subgroupsNameList.length > 0) {
        this.subgroupNameListFirstDateUpdated = this.subgroupsNameList[0]['Date Updated'];
      }
      this.previewTableLoading = false;
    },

    getGLAccountList: async function() {
      this.glAccCodeList = [];
      const result = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GLACCOUNT' }));
      this.glAccCodeList = result.map((item) => ({
        ...item,
        accCodeText: `${item['Acc No']} - ${item['Description']}`,
      }));
    },

    // API Call for Charge Code
    getChargeCodeListMaster: async function () {
      this.chargeCodeTableBoxLoading = true;
      this.chargeCodeList = [];
      this.chargeCodeListTableBoxSelectedRow = [];
      this.chargeCodeList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CHARGESCODE' });
      this.chargeCodeTableBoxLoading = false;
    },

    // API Call for Account Type
    getAccIndList: async function () {
      this.accIndList = [];
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GETACCIND' });
      this.accIndList = result;
    },

    getGLCostDeptList: async function () {
      this.glCostDeptList = [];
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GETGLCOSTDEPTNAME' });
      this.glCostDeptList = result;
    },

    switchPopupEntryFieldShown() {
      this.showGroup = this.selectedCategory.key === 'group';
      this.showGLAccount = this.selectedCategory.key === 'glAccount';
      this.showSubtotal = this.selectedCategory.key === 'subtotal';

      this.resetPopupEntryFields();

      if (this.showGroup || this.showSubtotal) {
        this.refreshPreviewTable();
        this.generateNewGroupPreview();
      } else if (this.showGLAccount) {
        this.popupEntryTitle = 'New GL Account';
        this.getAccIndList();
        this.getGLCostDeptList();
        this.glAccountOpeningDateValue = this.selectedYear ? this.fYearStartDate : null; //set to first day of fYear
      }
    },

    async handleCategoryChange(newCategory) {
      if (!this.selectedCategory) {
        // selectedCategory is null, skipped checking
        this.selectedCategory = newCategory;
        this.switchPopupEntryFieldShown();
        return;
      }

      let formName = '';
      let hasUnsavedChanges = false;
      if (this.showGroup) {
        formName = 'Group';
        hasUnsavedChanges =
          this.groupCodeInputValue != '' ||
          this.groupTitleInputValue != '' ||
          this.groupDescription ||
          this.selectedTransactionType;
      } else if (this.showSubtotal) {
        formName = 'Subtotal';
        hasUnsavedChanges = this.subtotalDescription != '' || this.subtotalOperation != '';
      }

      if (formName && hasUnsavedChanges) {
        const response = await this.$showAlert(
          'warning',
          'Discard Changes?',
          `You have unsaved changes in ${formName}. <br> Are you sure you want to switch?`,
          [{ text: 'Discard', textColor: 'white', color: 'red' }, { text: 'Cancel' }],
        );

        if (response === 'Cancel') {
          // Reverting back to the existing selection
          if (this.showGroup) {
            this.selectedCategory = this.categoryList[0];
          } else if (this.showGLAccount) {
            this.selectedCategory = this.categoryList[1];
          } else if (this.showSubtotal) {
            this.selectedCategory = this.categoryList[2];
          }
          return;
        }
      }
      this.selectedCategory = newCategory;
      this.switchPopupEntryFieldShown();
    },

    setDefaultBalanceType: function () {
      if (!this.selectedBalanceType) {
        this.selectedBalanceType = 
          this.balanceTypeList.getSelected(this.selectedGLTransactionType['TxnType'], 'value');
      }
    },

    convertToArray(item) {
      if (!item) return [];
      return Array.isArray(item) ? item : [item];
    },

    populatePopupEntryFields: function (details) {
      this.glAccountCodeInputValue = this.populateTextTrim(details.GLMaster['accno']);
      this.glAccountDescriptionInputValue = this.populateTextTrim(details.GLMaster['Description']);
      if (details.GLMaster['GL Date'] && new Date(details.GLMaster['GL Date']) != 'Invalid Date') {
        this.glAccountOpeningDateValue = new Date(details.GLMaster['GL Date']);
      }
      if (this.selectedReportType['Report'] == 'Profit & Loss') {
        this.selectedAccIndValue =
          details.GLMaster['Account Type'] ?
            this.populateObject(
              this.accIndList,
              'AccIndDesc',
              details.GLMaster['Account Type'].charAt(0).toUpperCase() +
                details.GLMaster['Account Type'].slice(1).toLowerCase(),
            )
          : null;
        this.selectedGLCostDeptListValue =
          details.GLMaster['Department'] ?
            this.populateObject(this.glCostDeptList, 'GLDept', details.GLMaster['Department'])
          : null;
      }
      this.selectedGLTransactionType =
        details.GLMaster['Transaction Type'] ?
          this.populateObject(
            this.transactionTypeList,
            'TxnTypeDesc',
            details.GLMaster['Transaction Type'].charAt(0).toUpperCase() +
              details.GLMaster['Transaction Type'].slice(1).toLowerCase(),
          )
        : null;
      this.selectedDefaultTaxCode =
        details.GLMaster['Default Tax Code'] ?
          this.populateObject(this.defaultTaxCodeList, 'Tax Code', details.GLMaster['Default Tax Code'])
        : null;
      this.selectedDefaultTaxGLAccCode =
        details.GLMaster['Default Tax Acc'] ?
          this.populateObject(this.glAccCodeList, 'Acc No', details.GLMaster['Default Tax Acc'])
        : null;
      this.selectedGainGLAccCode =
        details.GLMaster['Gain GL Acc Code'] ?
          this.populateObject(this.glAccCodeList, 'Acc No', details.GLMaster['Gain GL Acc Code'])
        : null;
      this.selectedLossGLAccCode =
        details.GLMaster['Loss GL Acc Code'] ?
          this.populateObject(this.glAccCodeList, 'Acc No', details.GLMaster['Loss GL Acc Code'])
        : null;
      this.selectedGroupUnder =
        details.GLMaster['GL Statement Format']['Group Under'] ?
          this.populateObject(
            this.groupUnderList,
            'Group Description',
            details.GLMaster['GL Statement Format']['Group Under'].toString(),
          )
        : null;
      this.selectedSubAccount =
        details.GLMaster['GL Statement Format']['Sub Account Of'] ?
          this.populateObject(this.glAccCodeList, 'Acc No', details.GLMaster['GL Statement Format']['Sub Account Of'])
        : null;
      this.selectedFixedAsset =
        details.GLMaster['Fixed Assets (Net Book Value)'] ?
          this.populateObject(this.glAccCodeList, 'Acc No', details.GLMaster['Fixed Assets (Net Book Value)'])
        : null;
      this.selectedBalanceType =
        details.GLMaster['Balance Type'] ?
          this.populateObject(this.balanceTypeList, 'value', details.GLMaster['Balance Type'])
        : null;
      this.selectedClassification =
        details.GLMaster['Classification'] ?
          this.populateObject(this.GLMasterClassListMaster, 'Classification Code', details.GLMaster['Classification'])
        : null;
      this.selectedMSICCode =
        details.GLMaster['Major Industry Code'] ?
          this.populateObject(this.MSICData, 'MSIC Code', details.GLMaster['Major Industry Code'])
        : null;
      this.openingBalanceValue = this.populateTextTrim(details.GLMaster['Opening Balance']);
      this.balanceCFValue = this.populateTextTrim(details.GLMaster['Balance C/F']);

      this.selectedARChargeCodeList = this.convertToArray(details.GLMaster['AR Charges Code']);
      this.selectedAPChargeCodeList = this.convertToArray(details.GLMaster['AP Charges Code']);
    },

    checkPopupEntryFields: function () {
      this.clearPopupEntryMessages();
      let error = false;

      if (this.popupEntryTitle[0] === 'N' && !this.selectedCategory) {
        //only check if it's New entry
        this.categoryErr = 'Record Type is Required!';
        this.popupEntryRequiredError.push({ key: 'categoryErr', value: 'Record Type is Required!' });
        error = true;
      }

      if (this.showGroup) {
        if (this.groupCodeInputValue.trim() === '') {
          this.groupCodeErr = 'Group Code is Required!';
          this.popupEntryRequiredError.push({ key: 'groupCodeErr', value: 'Group Code is Required!' });
          error = true;
        } else if (this.groupCodeInputValue.length > 4) {
          this.groupCodeErr = 'Group Code cannot exceed 4 characters!';
          this.popupEntryRequiredError.push({
            key: 'groupCodeErr',
            value: 'Group Code cannot exceed 4 characters!',
          });
          error = true;
        } else {
          const dupeGroupCode = this.subgroupsNameList
            .map((m) => m['Group Code'].toString())
            .includes(this.groupCodeInputValue);
          if (this.popupEntryTitle === 'Group Settings' && dupeGroupCode) {
            this.groupCodeErr = 'Group Code already exists!';
            this.popupEntryRequiredError.push({
              key: 'groupCodeErr',
              value: 'Group Code already exists!',
            });
            error = true;
          }
        }

        if (this.groupTitleInputValue.trim() === '') {
          this.groupTitleErr = 'Group Title in Financial Statement is Required!';
          this.popupEntryRequiredError.push({
            key: 'groupTitleErr',
            value: 'Group Title in Financial Statement is Required!',
          });
          error = true;
        } else if (this.groupTitleInputValue.length > 50) {
          this.groupTitleErr = 'Group Title in Financial Statement cannot exceed 50 characters!';
          this.popupEntryRequiredError.push({
            key: 'groupTitleErr',
            value: 'Group Title in Financial Statement cannot exceed 50 characters!',
          });
          error = true;
        } else {
          const dupeGroupTitle = this.subgroupsNameList
            .filter((f) => f['Group Code'] != this.groupCodeInputValue && f['Operation'] != this.groupCodeInputValue)
            .some((group) => group['Group Title in Financial Statement'].trim().toLowerCase() === this.groupTitleInputValue.trim().toLowerCase());
          if (dupeGroupTitle) {
            this.groupTitleErr = 'Group Title in Financial Statement already exists!';
            this.popupEntryRequiredError.push({
              key: 'groupTitleErr',
              value: 'Group Title in Financial Statement already exists!',
            });
            error = true;
          }
        }

        if (this.groupDescription.trim() === '') {
          this.groupDescriptionErr = 'Group Description is Required!';
          this.popupEntryRequiredError.push({ key: 'groupDescriptionErr', value: 'Group Description is Required!' });
          error = true;
        } else if (this.groupDescription.length > 100) {
          this.groupDescriptionErr = 'Group Description cannot exceed 50 characters!';
          this.popupEntryRequiredError.push({
            key: 'groupDescriptionErr',
            value: 'Group Description cannot exceed 50 characters!',
          });
          error = true;
        } else {
          const dupeGroupDescription = this.subgroupsNameList
            .filter((f) => f['Group Code'] != this.groupCodeInputValue && f['Operation'] != this.groupCodeInputValue)
            .some((group) => group['Group Title in Financial Statement'].trim().toLowerCase() === this.groupDescription.trim().toLowerCase());
          if (dupeGroupDescription) {
            this.groupDescriptionErr = 'Group Description already exists!';
            this.popupEntryRequiredError.push({
              key: 'groupDescriptionErr',
              value: 'Group Description already exists!',
            });
            error = true;
          }
        }

        if (!this.selectedTransactionType) {
          this.groupTransactionTypeErr = 'Transaction Type is Required!';
          this.popupEntryRequiredError.push({
            key: 'groupTransactionTypeErr',
            value: 'Transaction Type is Required!',
          });
          error = true;
        }

        if (
          (!this.beforeAfterDropdownValue && this.existingGroupListValue) ||
          (this.beforeAfterDropdownValue && !this.existingGroupListValue)
        ) {
          if (!this.beforeAfterDropdownValue) {
            this.beforeAfterDropdownValueErr = 'Before / After is Required!';
            this.popupEntryRequiredError.push({
              key: 'beforeAfterDropdownValueErr',
              value: 'Before / After is Required!',
            });
          }
          if (!this.existingGroupListValue) {
            this.existingGroupListValueErr = 'Existing Group List Value is Required!';
            this.popupEntryRequiredError.push({
              key: 'existingGroupListValueErr',
              value: 'Existing Group List Value is Required!',
            });
          }
          error = true;
        }
      } else if (this.showGLAccount) {
        if (this.glAccountCodeInputValue.trim() === '') {
          this.glAccountCodeInputValueErr = 'GL Account Code is Required!';
          this.popupEntryRequiredError.push({
            key: 'glAccountCodeInputValueErr',
            value: 'GL Account Code is Required!',
          });
          error = true;
        } else if (this.glAccountCodeInputValue.length > 20) {
          this.glAccountCodeInputValueErr = 'GL Account Code cannot exceed 20 characters!';
          this.popupEntryRequiredError.push({
            key: 'glAccountCodeInputValueErr',
            value: 'GL Account Code cannot exceed 20 characters!',
          });
          error = true;
        }
        if (this.glAccountDescriptionInputValue.trim() === '') {
          this.glAccountDescriptionInputValueErr = 'GL Account Description is Required!';
          this.popupEntryRequiredError.push({
            key: 'glAccountDescriptionInputValueErr',
            value: 'GL Account Description is Required!',
          });
          error = true;
        } else if (this.glAccountDescriptionInputValue.length > 100) {
          this.glAccountDescriptionInputValueErr = 'GL Account Description cannot exceed 100 characters!';
          this.popupEntryRequiredError.push({
            key: 'glAccountDescriptionInputValueErr',
            value: 'GL Account Description cannot exceed 100 characters!',
          });
          error = true;
        }
        if (!this.glAccountOpeningDateValue) {
          this.glAccountOpeningDateValueErr = 'Opening Date is Required!';
          this.popupEntryRequiredError.push({
            key: 'glAccountOpeningDateValueErr',
            value: 'Opening Date is Required!',
          });
          error = true;
        }
        if (this.selectedReportType['Report'] == 'Profit & Loss') {
          if (!this.selectedAccIndValue) {
            this.selectedAccIndErr = 'Account Type is Required!';
            this.popupEntryRequiredError.push({
              key: 'selectedAccIndErr',
              value: 'Account Type is Required!',
            });
            error = true;
          }
          if (!this.selectedGLCostDeptListValue) {
            this.selectedGLCostDeptListErr = 'Cost Department is Required!';
            this.popupEntryRequiredError.push({
              key: 'selectedGLCostDeptListErr',
              value: 'Cost Department is Required!',
            });
            error = true;
          }
        }
        if (!this.selectedGLTransactionType) {
          this.selectedGLTransactionTypeErr = 'Transaction Type is Required!';
          this.popupEntryRequiredError.push({
            key: 'selectedGLTransactionTypeErr',
            value: 'Transaction Type is Required!',
          });
          error = true;
        }
        if (!this.selectedGroupUnder) {
          this.selectedGroupUnderErr = 'Group Under is Required!';
          this.popupEntryRequiredError.push({ key: 'selectedGroupUnderErr', value: 'Group Under is Required!' });
          error = true;
        }

        if (!this.selectedBalanceType) {
          this.selectedBalanceTypeErr = 'Balance Type is Required!';
          this.popupEntryRequiredError.push({ key: 'selectedBalanceTypeErr', value: 'Balance Type is Required!' });
          error = true;
        }
      } else if (this.showSubtotal) {
        if (!this.subtotalDescription || this.subtotalDescription == '') {
          this.subtotalDescriptionErr = 'Description is Required!';
          this.popupEntryRequiredError.push({ key: 'subtotalDescription', value: 'Description is Required!' });
          error = true;
        } else if (
          this.previewGroupTableData
            .filter((f) => f['Seq'] != this.subtotalSeq)
            .some((s) => s['Group Title in Financial Statement'].trim().toLowerCase() == this.subtotalDescription.trim().toLowerCase())
        ) {
          this.subtotalDescriptionErr = 'Subtotal Description already exists!';
          this.popupEntryErrorMessages.push('Subtotal Description already exists!');
          error = true;
        }

        if (!this.subtotalOperation || this.subtotalOperation == '') {
          this.subtotalOperationErr = 'Operation is Required!';
          this.popupEntryErrorMessages.push('Operation is Required!');
          error = true;
        }

        if (
          this.subtotalOperation.startsWith('+') ||
          this.subtotalOperation.startsWith('-') ||
          this.subtotalOperation.endsWith('+') ||
          this.subtotalOperation.endsWith('-')
        ) {
          this.subtotalOperationErr = 'Operation cannot start or end with + or -';
          this.popupEntryErrorMessages.push('Operation cannot start or end with + or -');
          error = true;
        }

        if (/[+\\-]{2,}/.test(this.subtotalOperation)) {
          this.subtotalOperationErr = 'Operation cannot have + or - right next to each other';
          this.popupEntryErrorMessages.push('Operation cannot have + or - right next to each other');
          error = true;
        }

        if (this.hasArithmeticOperation(this.subtotalOperation) == false) {
          this.subtotalOperationErr = 'Operation must have + or - with multiple subgroup';
          this.popupEntryErrorMessages.push('Operation must have + or - with multiple subgroup');
          error = true;
        }

        if (
          this.subtotalOperation != '' &&
          this.previewGroupTableData
            .filter((f) => f['Seq'] != this.subtotalSeq)
            .some((s) => s['Operation'] == this.subtotalOperation)
        ) {
          this.subtotalOperationErr = 'Subtotal operation group cannot be duplicated!';
          this.popupEntryErrorMessages.push('Subtotal operation group cannot be duplicated!');
          error = true;
        }

        //check if the group existed
        if (this.subtotalOperationErr == '') {
          const existingGroupValues = this.previewGroupTableData
            .filter((f) => f['Record Type'] == 'Subgroup')
            .map((m) => m['Group Code']);
          const operationGroupValues = this.subtotalOperation.split(/[+\\-]/);
          operationGroupValues.forEach((f) => {
            if (!existingGroupValues.includes(f)) {
              this.subtotalOperationErr = `${f} is not a valid group code`;
              this.popupEntryErrorMessages.push(`${f} is not a valid group code`);
              error = true;
            }
          });
        }
      }

      return error;
    },

    clearPopupEntryMessages: function () {
      this.popupEntryRequiredError = [];
      this.popupEntrySuccessMessages = [];
      this.popupEntryMessages = [];
      this.popupEntryErrorMessages = [];
    },

    resetPopupEntryFields: function () {
      this.menuNewButtonClicked = true;

      // For PopupEntry Fields
      this.categoryErr = '';

      // Group Fields
      this.groupCodeInputValue = '';
      this.groupDescription = '';
      this.groupTitleInputValue = '';
      this.selectedTransactionType = null;
      this.beforeAfterDropdownValue = null;
      this.existingGroupListValue = null;
      this.selectedSubgroupsTableBoxRow = [];
      this.groupTitleErr = '';
      this.groupCodeErr = '';
      this.groupDescriptionErr = '';
      this.groupTransactionTypeErr = null;

      // GL Account Fields
      this.glAccountCodeInputValue = '';
      this.glAccountDescriptionInputValue = '';
      this.glAccountOpeningDateValue = null;
      this.selectedGLTransactionType = null;
      this.selectedDefaultTaxCode = null;
      this.selectedDefaultTaxGLAccCode = null;
      this.selectedGainGLAccCode = null;
      this.selectedLossGLAccCode = null;
      this.selectedGroupUnder = null;
      this.selectedSubAccount = null;
      this.selectedFixedAsset = null;
      this.selectedClassification = null;
      this.selectedMSICCode = null;
      this.selectedARChargeCodeList = [];
      this.selectedAPChargeCodeList = [];
      this.selectedARChargeCodeTableRow = [];
      this.selectedAPChargeCodeTableRow = [];
      this.selectedBalanceType = null;
      this.openingBalanceValue = '0';
      this.balanceCFValue = '';
      this.selectedAccIndValue = null;
      this.selectedGLCostDeptListValue = null;

      this.selectedARChargeCode = [];

      this.glAccountCodeInputValueErr = '';
      this.glAccountDescriptionInputValueErr = '';
      this.glAccountOpeningDateValueErr = '';
      this.selectedAccIndErr = '';
      this.selectedGLCostDeptListErr = '';
      this.selectedGLTransactionTypeErr = null;
      this.selectedDefaultTaxGLAccCodeErr = null;
      this.selectedGainGLAccCodeErr = null;
      this.selectedLossGLAccCodeErr = null;
      this.selectedGroupUnderErr = null;
      this.selectedSubAccountErr = null;
      this.selectedFixedAssetErr = null;
      this.selectedBalanceTypeErr = '';

      this.showARChargeCodeInput = false;
      this.showAPChargeCodeInput = false;

      // Subtotal Fields
      this.operationList = [];
      this.subtotalSeq = '';
      this.subtotalDescription = '';
      this.subtotalDescriptionErr = '';
      this.subtotalOperation = '';
      this.subtotalOperationErr = '';
    },

    popupEntryRequiredClicked: function (item) {
      this.$nextTick(() => {
        this.$refs[item].setFocus();
      });
    },

    popupEntrySaveClicked: async function () {
      this.$fullscreenLoading('Saving...');
      if (this.popupEntryTitle.toUpperCase() == 'GROUP SETTINGS') {
        //go to group
        await this.saveGroupClicked();
      } else {
        //go to gl master
        await this.saveGLClicked();
      }
      this.$fullscreenLoading('');
    },

    saveGroupClicked: async function () {
      //saving group function
      this.$fullscreenLoading('Saving Group Settings');
      const jsonBody = {
        rpt: this.selectedReportType['rptType'],
        lastupdatetime: this.subgroupNameListFirstDateUpdated,
        group: this.groupChangesArray,
        grouporder: this.previewGroupTableData.map((r) => ({
          type: r['Record Type'],
          operation: r['Operation'],
          grouptitle: r['Group Title in Financial Statement'],
          groupcode: r['Group Code'],
        })),
      };

      const result = await this.$apiCallV2('/GLMaster/SaveGLMasterGroup', jsonBody);
      if (result[0]['Results'] != '0') {
        this.popupEntryErrorMessages.push(`There was an error saving: ${result[0]['ErrMsg']}`);
      } else {
        this.popupEntrySuccessMessages.push(`Succesfully saved group settings. ${result[0]['ErrMsg']}`);
        await this.getReportTypeData();
      }
      this.$fullscreenLoading();
    },

    saveGLClicked: async function () {
      //saving gl master function
      const isInvalid = this.checkPopupEntryFields();
      if (isInvalid) {
        return;
      }

      const action = this.popupEntryTitle[0];
      const requestBody = this.formatSavePayloadBody(action);
      this.$fullscreenLoading('Saving');

      const result = await this.$apiCallV2('/GLMaster/SaveGLMaster', requestBody);
      if (result[0]['Results'] != '0') {
        if (action == 'N') {
          this.popupEntryErrorMessages.push(`There was an error saving: ${result[0]['ErrMsg']}`);
        } else {
          this.popupEntryErrorMessages.push(`There was an error editing: ${result[0]['ErrMsg']}`);
        }
        this.$fullscreenLoading();
        return;
      }

      // Save Completed
      this.viewMode = true;
      this.viewModePK = true;
      this.popupEntryTitle = 'View GL Account';
      this.resetPopupEntryFields();
      this.$fullscreenLoading('Refreshing Report');

      if (this.selectedReportType['Report'] == 'Profit & Loss') {
        await this.getPNLAnalysisTableData(this.selectedYear['Fyear']);
        await this.getPNLSubgroupNames();
      } else {
        await this.getBSAnalysisTableData(this.selectedYear['Fyear']);
        await this.getBSSubgroupNames();
      }
      this.$fullscreenLoading('Getting Details');
      await this.getGLAccountList(); //refresh the gl account list to get new acc no if added
      const details = await this.getGLAccountDetails(requestBody.accno);
      this.populatePopupEntryFields(details);

      if (action == 'N') {
        this.popupEntrySuccessMessages.push(`Successfully Created GL Account: ${this.glAccountCodeInputValue}`);
      } else if (action == 'E' && this.popupEntryErrorMessages.length === 0) {
        this.popupEntrySuccessMessages.push(`Successfully Edited GL Account: ${this.glAccountCodeInputValue}`);
      }

      this.pnlMasterTableBoxSelectedRow = [];
      this.bsMasterTableBoxSelectedRow = [];
      this.$fullscreenLoading();
    },

    formatSavePayloadBody: function (action) {
      const payloadBody = {
        functionkey: 'GLACC',
        action: action,
        subgroup: this.selectedGroupUnder['Group Description'],
        gldate: this.glAccountOpeningDateValue.formatString('yyyy-MM-dd'),
        fyear: this.selectedYear['Fyear'],
        accno: this.glAccountCodeInputValue.trim(),
        accdescr: this.glAccountDescriptionInputValue.trim(),
        acctype: this.selectedReportType['Report'],
        txntype: this.selectedGLTransactionType['TxnTypeDesc'],
        gst2taxcode: this.selectedDefaultTaxCode?.['Tax Code'],
        classification: this.selectedClassification?.['Classification Code'],
        gstmsic: this.selectedMSICCode?.['MSIC Code'],
        gst2acc: this.selectedDefaultTaxGLAccCode?.['Acc No'],
        fcurgainglacc: this.selectedGainGLAccCode?.['Acc No'],
        fcurlossglacc: this.selectedLossGLAccCode?.['Acc No'],
        groupac: this.selectedSubAccount?.['Acc No'] ?? '',
        frmacc: this.selectedFixedAsset?.['Acc No'] ?? '',
        openind: this.selectedBalanceType['value'],
        openbal: this.openingBalanceValue || null,
        ARarr: this.arChargeCodeListWithoutDeleted.map((chargeCode) => ({
          archargescode: chargeCode['Charge Code'],
        })),
        AParr: this.apChargeCodeListWithoutDeleted.map((chargeCode) => ({
          apchargescode: chargeCode['Charge Code'],
        })),
        accind: this.selectedAccIndValue ? this.selectedAccIndValue['AccIndDesc'] : null,
        deptcode: this.selectedGLCostDeptListValue ? this.selectedGLCostDeptListValue['GLDept'] : null,
      };

      if (payloadBody.openind === '0') {
        payloadBody.total_debit = this.openingBalanceValue ? this.openingBalanceValue : '0';
        payloadBody.total_credit = 0;
      } else if (payloadBody.openind === '1') {
        payloadBody.total_debit = 0;
        payloadBody.total_credit = this.openingBalanceValue ? this.openingBalanceValue : '0';
      }

      return payloadBody;
    },

    populateTextTrim: function (text) {
      // To populate text with trim
      return text ? text.trim() : '';
    },

    populateObject: function (data, key, compare) {
      // To populate object into dropdown or input query
      try {
        return data.find((d) => (d[key]?.trim?.() ?? d[key]) == (compare?.trim?.() ?? compare));
      } catch (error) {
        return null;
      }
    },

    handleMenuClick: async function (menu) {
      this.clearPopupEntryMessages();
      this.resetPopupEntryFields();

      // Prompt user to select Financial Year and Statement if left unselected
      if (!this.selectedYear || !this.selectedReportType) {
        this.$showAlert(
          'error',
          'Invalid Financial Year or Statement',
          'Please select a value for Financial Year AND Financial Statement',
        );
        return;
      }

      switch (menu.title) {
        case 'New':
          this.popupEntryTitle = 'New GL Account';
          this.viewMode = false;
          this.viewModePK = false;
          this.showPopupEntry = true;
          this.showGroup = false;
          this.showGLAccount = true;
          this.showSubtotal = false;
          this.showReorder = false;
          this.menuNewButtonClicked = true;
          this.selectedCategory = this.computedCategoryList.getSelected('glAccount', 'key');
          this.switchPopupEntryFieldShown();
          break;

        case 'Edit': {
          this.menuNewButtonClicked = false;
          this.showReorder = false;

          if (this.pnlMasterTableBoxSelectedRow.length === 0 && this.bsMasterTableBoxSelectedRow.length === 0) {
            this.$showAlert('error', 'No Record Selected', 'Please select a record to edit');
            break;
          }

          const isPNL = this.selectedReportType['Report'] == 'Profit & Loss';
          const selectedRecord = isPNL ?
              this.pnlMasterTableBoxSelectedRow[0]
            : this.bsMasterTableBoxSelectedRow[0];
          if (selectedRecord && selectedRecord[isPNL ? 'PNLSeq' : 'BSSeq'] % 1 !== 0) {
            this.$showAlert(
              'error',
              'Group Record Selected',
              'Please use Group Settings to edit group related records',
            );
            break;
          }

          if (this.selectedReportType['Report'] == 'Profit & Loss') {
            this.$fullscreenLoading('Getting Details');
            await this.getRecordCategoryForPopulate(this.pnlMasterTableBoxSelectedRow[0]);
            this.$fullscreenLoading();
          }
          if (this.selectedReportType['Report'] == 'Balance Sheet') {
            this.$fullscreenLoading('Getting Details');
            await this.getRecordCategoryForPopulate(this.bsMasterTableBoxSelectedRow[0]);
            this.$fullscreenLoading();
          }

          this.popupEntryTitle = 'Edit GL Account';
          this.viewMode = false;
          this.viewModePK = true;
          this.showPopupEntry = true;
          break;
        }

        case 'Delete': {
          if (this.pnlMasterTableBoxSelectedRow.length === 0 && this.bsMasterTableBoxSelectedRow.length === 0) {
            this.$showAlert('error', 'No Record Selected', 'Please select a record to delete');
            break;
          }

          const deleteRecord =
            this.selectedReportType['Report'] == 'Profit & Loss' ?
              this.pnlMasterTableBoxSelectedRow[0]
            : this.bsMasterTableBoxSelectedRow[0];
          let rptSeq = '';
          if (deleteRecord) {
            rptSeq =
              this.selectedReportType['Report'] == 'Profit & Loss' ? deleteRecord['PNLSeq'] : deleteRecord['BSSeq'];
          }
          if (deleteRecord && rptSeq % 1 !== 0) {
            this.$showAlert(
              'error',
              'Group Record Selected',
              'Please use Group Settings to delete group related records',
            );
            break;
          }

          const deleteConfirmation = await this.$showAlert(
            'warning',
            'Delete GL Account',
            `Are you sure you want to delete ${deleteRecord['GL Account Code']}`,
            [{ text: 'Delete', textColor: 'white', color: 'red' }, { text: 'Cancel' }],
          );
          if (deleteConfirmation == 'Cancel') {
            break;
          }
          this.$fullscreenLoading('Getting Details');
          await this.getRecordCategoryForPopulate(deleteRecord);

          this.$fullscreenLoading('Deleting GL Account');
          const result = await this.$apiCallV2('/GLMaster/SaveGLMaster', this.formatSavePayloadBody('D'));
          if (result[0]['Results'] != '0') {
            // Delete error
            this.$showAlert(
              'error',
              'GL Account NOT Deleted',
              `There was an error deleting GL Account ${deleteRecord['GL Account Code']}`,
            );
            break;
          }

          this.pnlMasterTableBoxSelectedRow = [];
          this.$fullscreenLoading('Loading Data');
          if (this.selectedReportType['Report'] == 'Profit & Loss') {
            await this.getPNLAnalysisTableData(this.selectedYear['Fyear']);
            await this.getPNLSubgroupNames();
          } else {
            await this.getBSAnalysisTableData(this.selectedYear['Fyear']);
            await this.getBSSubgroupNames();
          }
          await this.getGLAccountList(); //refresh the gl account list after deleted a gl account
          this.$fullscreenLoading();
          break;
        }
        case 'View': {
          if (this.pnlMasterTableBoxSelectedRow.length === 0 && this.bsMasterTableBoxSelectedRow.length === 0) {
            this.$showAlert('error', 'No Record Selected', 'Please select a record to view');
            break;
          }

          const viewRecord =
            this.selectedReportType['Report'] == 'Profit & Loss' ?
              this.pnlMasterTableBoxSelectedRow[0]
            : this.bsMasterTableBoxSelectedRow[0];
          let rptSeq = '';
          if (viewRecord) {
            rptSeq = this.selectedReportType['Report'] == 'Profit & Loss' ? viewRecord['PNLSeq'] : viewRecord['BSSeq'];
          }

          if (viewRecord && rptSeq % 1 !== 0) {
            this.$showAlert(
              'error',
              'Group Record Selected',
              'Please use Group Settings to view group related records',
            );
            break;
          }

          this.$fullscreenLoading('Getting Details');
          await this.getRecordCategoryForPopulate(viewRecord);
          this.$fullscreenLoading();

          this.popupEntryTitle = 'View GL Account';
          this.viewMode = true;
          this.viewModePK = true;
          this.showPopupEntry = true;
          break;
        }
        case 'Group Settings': {
          this.$fullscreenLoading('Getting Data');
          if (this.selectedReportType['Report'] == 'Profit & Loss') {
            await this.getPNLSubgroupNames();
          } else {
            await this.getBSSubgroupNames();
          }

          this.popupEntryTitle = 'Group Settings';
          this.viewMode = false;
          this.viewModePK = false;
          this.showPopupEntry = true;
          this.showGroup = false;
          this.showGroupNewEdit = false;
          this.showGLAccount = false;
          this.showSubtotal = false;
          this.showReorder = false;
          this.menuNewButtonClicked = true;
          this.selectedCategory = null;
          this.generateNewGroupPreview();
          this.showPreviewTable = true;
          this.$fullscreenLoading('');
          break;
        }
      }
    },

    clearARChargeCodeInputs: function () {
      this.selectedARChargeCode = [];
      this.arChargeCodeErr = '';
    },

    addARChargeCodeButtonClicked: function () {
      if (this.showAPChargeCodeInput) {
        this.clearAPChargeCodeInputs();
        this.showAPChargeCodeInput = false;
        this.clearPopupEntryMessages();
      }
      this.clearARChargeCodeInputs();
      this.showARChargeCodeInput = true;
    },

    deleteARChargeCodeButtonClicked: async function () {
      if (this.selectedARChargeCodeTableRow.length === 0) {
        this.$showAlert('error', 'No AR Charge Code is selected', 'Please select an AR Charge Code to delete');
        return;
      }

      const deleteConfirmation = await this.$showAlert(
        'warning',
        'Delete AR Charge Code',
        `Are you sure you want to delete AR Charge Code? <b>${this.selectedARChargeCodeTableRow[0]['Charge Code']}</b>?`,
        [{ text: 'Delete', textColor: 'white', color: 'red' }, { text: 'Cancel' }],
      );
      if (deleteConfirmation === 'Cancel') {
        return;
      }

      const arChargeCodeToDelete = this.selectedARChargeCodeTableRow[0];
      let matchedIndex;
      if (arChargeCodeToDelete.ID !== undefined && arChargeCodeToDelete.ID !== null) {
        const foundIndex = this.selectedARChargeCodeList.findIndex(
          (arChargeCodeData) => arChargeCodeData.ID === arChargeCodeToDelete.ID,
        );
        if (foundIndex !== -1) {
          matchedIndex = foundIndex;
        }
      }
      if (matchedIndex === undefined && arChargeCodeToDelete._rowid) {
        const foundIndex = this.selectedARChargeCodeList.findIndex(
          (arChargeCodeData) => arChargeCodeData._rowid === arChargeCodeToDelete._rowid,
        );
        if (foundIndex !== -1) {
          matchedIndex = foundIndex;
        }
      }
      if (matchedIndex === undefined) {
        this.$showAlert('error', 'No matched index found for Group to delete', 'Group not found');
        return;
      }

      this.selectedARChargeCodeList.forEach((item) => {
        if (item['#'] > this.selectedARChargeCodeList[matchedIndex]['#']) {
          item['#'] = (parseInt(item['#']) - 1).toString();
        }
      });

      if (arChargeCodeToDelete._action === 'N') {
        this.selectedARChargeCodeList.splice(matchedIndex, 1);
      } else {
        this.selectedARChargeCodeList[matchedIndex]._action = 'D';
      }

      const modifiedARChargeCodeTableData = this.selectedARChargeCodeList.map((arChargeCodeData) => {
        delete arChargeCodeData._rowid;
        return arChargeCodeData;
      });
      this.showARChargeCodeTable = false;
      this.selectedARChargeCodeList = [];
      this.selectedARChargeCodeTableRow = [];
      this.$nextTick(() => {
        this.selectedARChargeCodeList = modifiedARChargeCodeTableData;
        this.showARChargeCodeTable = true;
      });
    },

    cancelARChargeCodeButtonClicked: function () {
      this.clearARChargeCodeInputs();
      this.showARChargeCodeInput = false;
      this.clearPopupEntryMessages();
    },

    addARChargeCode: function () {
      if (this.selectedARChargeCode.length === 0) {
        this.$showAlert('error', 'Charge Code is Required', 'Please select a Charge Code');
        return;
      }

      if (
        this.checkChargeCodeField(
          this.selectedARChargeCode,
          this.showARChargeCodeInput,
          this.selectedARChargeCodeList,
          this.selectedAPChargeCodeList,
        )
      ) {
        return;
      }

      const newChargeCodes = this.selectedARChargeCode.map((code, index) => ({
        '#': (this.arChargeCodeListWithoutDeleted.length + index + 1).toString(),
        'Charge Code': code['Charge Code'],
        Description: code['Description'],
        _action: 'N',
      }));

      // Update the table data
      this.showARChargeCodeTable = false;
      this.selectedARChargeCodeList = [...this.selectedARChargeCodeList, ...newChargeCodes];
      this.selectedARChargeCodeTableRow = [];
      this.showARChargeCodeInput = false;

      this.$nextTick(() => {
        this.showARChargeCodeTable = true;
      });
    },

    clearAPChargeCodeInputs: function () {
      this.selectedAPChargeCode = [];
      this.apChargeCodeErr = '';
    },

    addAPChargeCodeButtonClicked: function () {
      if (this.showARChargeCodeInput) {
        this.clearARChargeCodeInputs();
        this.showARChargeCodeInput = false;
        this.clearPopupEntryMessages();
      }
      this.clearAPChargeCodeInputs();
      this.showAPChargeCodeInput = true;
    },

    cancelAPChargeCodeButtonClicked: function () {
      this.clearAPChargeCodeInputs();
      this.showAPChargeCodeInput = false;
      this.clearPopupEntryMessages();
    },

    addAPChargeCode: function () {
      if (this.selectedAPChargeCode.length === 0) {
        this.$showAlert('error', 'Charge Code is Required', 'Please select a Charge Code');
        return;
      }
      if (
        this.checkChargeCodeField(
          this.selectedAPChargeCode,
          this.showAPChargeCodeInput,
          this.selectedAPChargeCodeList,
          this.selectedARChargeCodeList,
        )
      ) {
        return;
      }

      const newChargeCodes = this.selectedAPChargeCode.map((code, index) => ({
        '#': (this.apChargeCodeListWithoutDeleted.length + index + 1).toString(),
        'Charge Code': code['Charge Code'],
        Description: code['Description'],
        _action: 'N',
      }));

      // Update the table data
      this.showAPChargeCodeTable = false;
      this.selectedAPChargeCodeList = [...this.selectedAPChargeCodeList, ...newChargeCodes];
      this.selectedAPChargeCodeTableRow = [];
      this.showAPChargeCodeInput = false;

      this.$nextTick(() => {
        this.showAPChargeCodeTable = true;
      });
    },

    checkChargeCodeField: function (selectedChargeCodes, showChargeCodeInput, chargeCodeList, oppositeChargeCodeList) {
      this.clearPopupEntryMessages();

      let error = false;

      if (!selectedChargeCodes && showChargeCodeInput) {
        this.popupEntryErrorMessages.push('Charge Code is Required!');
        error = true;
      }

      // Prevent duplicates
      const existingCodes = new Set(chargeCodeList.map((item) => item['Charge Code']));
      const duplicateCodes = selectedChargeCodes.filter((code) => existingCodes.has(code['Charge Code']));

      if (duplicateCodes.length > 0) {
        this.popupEntryErrorMessages.push(
          `The following codes are already added: ${duplicateCodes.map((c) => c['Charge Code']).join(', ')}`,
        );
        error = true;
      }

      // Prevent adding charge codes that exist in the opposite list,
      const oppositeCodes = new Set(oppositeChargeCodeList.map((item) => item['Charge Code']));
      const conflictingCodes = selectedChargeCodes.filter((code) => oppositeCodes.has(code['Charge Code']));

      if (conflictingCodes.length > 0) {
        this.popupEntryErrorMessages.push(
          `The following charge codes already exist in the opposite charge code list: ${conflictingCodes.map((c) => c['Charge Code']).join(', ')}`,
        );
        error = true;
      }

      return error;
    },

    deleteAPChargeCodeButtonClicked: async function () {
      if (this.selectedAPChargeCodeTableRow.length === 0) {
        this.$showAlert('error', 'No AP Charge Code is selected', 'Please select an AP Charge Code to delete');
        return;
      }

      const deleteConfirmation = await this.$showAlert(
        'warning',
        'Delete AP Charge Code',
        `Are you sure you want to delete AP Charge Code? <b>${this.selectedAPChargeCodeTableRow[0]['Charge Code']}</b>?`,
        [{ text: 'Delete', textColor: 'white', color: 'red' }, { text: 'Cancel' }],
      );
      if (deleteConfirmation === 'Cancel') {
        return;
      }

      const apChargeCodeToDelete = this.selectedAPChargeCodeTableRow[0];
      let matchedIndex;
      if (apChargeCodeToDelete.ID !== undefined && apChargeCodeToDelete.ID !== null) {
        const foundIndex = this.selectedARChargeCodeList.findIndex(
          (apChargeCodeData) => apChargeCodeData.ID === apChargeCodeToDelete.ID,
        );
        if (foundIndex !== -1) {
          matchedIndex = foundIndex;
        }
      }
      if (matchedIndex === undefined && apChargeCodeToDelete._rowid) {
        const foundIndex = this.selectedAPChargeCodeList.findIndex(
          (apChargeCodeData) => apChargeCodeData._rowid === apChargeCodeToDelete._rowid,
        );
        if (foundIndex !== -1) {
          matchedIndex = foundIndex;
        }
      }
      if (matchedIndex === undefined) {
        this.$showAlert('error', 'No matched index found for Group to delete', 'Group not found');
        return;
      }

      this.selectedAPChargeCodeList.forEach((item) => {
        if (item['#'] > this.selectedAPChargeCodeList[matchedIndex]['#']) {
          item['#'] = (parseInt(item['#']) - 1).toString();
        }
      });

      if (apChargeCodeToDelete._action === 'N') {
        this.selectedAPChargeCodeList.splice(matchedIndex, 1);
      } else {
        this.selectedAPChargeCodeList[matchedIndex]._action = 'D';
      }

      const modifiedAPChargeCodeTableData = this.selectedAPChargeCodeList.map((apChargeCodeData) => {
        delete apChargeCodeData._rowid;
        return apChargeCodeData;
      });
      this.showAPChargeCodeTable = false;
      this.selectedAPChargeCodeList = [];
      this.selectedAPChargeCodeTableRow = [];
      this.$nextTick(() => {
        this.selectedAPChargeCodeList = modifiedAPChargeCodeTableData;
        this.showAPChargeCodeTable = true;
      });
    },

    refreshPreviewTable: function () {
      this.showPreviewTable = false;
      this.$nextTick(() => {
        this.showPreviewTable = true;
      });
    },

    previewMoveUpButtonClicked: function () {
      if (!this.selectedSubgroupsTableBoxRow.length) {
        this.$showAlert('error', 'No row selected', 'Please select a row to move.');
        return;
      }

      // Find index of the selected row
      const selectedRow = this.selectedSubgroupsTableBoxRow[0];
      let groupIndex;
      let subtotalIndex;

      if (selectedRow['Group Code']) {
        groupIndex = this.previewGroupTableData.findIndex((row) => row['Group Code'] == selectedRow['Group Code']);
        subtotalIndex = this.previewGroupTableData.findIndex((row) => row['Operation'] == selectedRow['Group Code']);
      } else if (this.hasArithmeticOperation(selectedRow['Operation'])) {
        groupIndex = this.previewGroupTableData.findIndex(
          (row) => row['Group Title in Financial Statement'] === selectedRow['Group Title in Financial Statement'],
        );
        subtotalIndex = this.previewGroupTableData.findIndex((row) => row['Operation'] == selectedRow['Operation']);
      } else {
        groupIndex = this.previewGroupTableData.findIndex((row) => row['Group Code'] == selectedRow['Operation']);
        subtotalIndex = this.previewGroupTableData.findIndex((row) => row['Operation'] == selectedRow['Operation']);
      }

      const groupRows = this.previewGroupTableData.slice(groupIndex, subtotalIndex + 1);

      let targetIndex;
      if (groupIndex < 2) {
        return;
      }

      if (
        this.previewGroupTableData[groupIndex]['Record Type'] !=
        this.previewGroupTableData[groupIndex - 1]['Record Type']
      ) {
        if (
          this.previewGroupTableData[groupIndex - 1]['Operation'] ==
          this.previewGroupTableData[groupIndex - 2]['Group Code']
        ) {
          targetIndex = groupIndex - 2;
        } else {
          targetIndex = groupIndex - 1;
        }
      } else if (this.hasArithmeticOperation(selectedRow['Operation'])) {
        if (
          this.previewGroupTableData[groupIndex]['Record Type'] !=
          this.previewGroupTableData[groupIndex - 2]['Record Type']
        ) {
          targetIndex = groupIndex - 2;
        } else {
          targetIndex = groupIndex - 1;
        }
      }
      if (targetIndex < 0) {
        return;
      }

      // Remove the block from the current position
      this.previewGroupTableData.splice(groupIndex, groupRows.length);

      // Insert the block at the new position
      this.previewGroupTableData.splice(targetIndex, 0, ...groupRows);

      // Update selection to reflect the new position
      this.selectedSubgroupsTableBoxRow = [this.previewGroupTableData[targetIndex]];

      this.refreshPreviewTable();
    },

    previewMoveDownButtonClicked: function () {
      if (!this.selectedSubgroupsTableBoxRow.length) {
        this.$showAlert('error', 'No row selected', 'Please select a row to move.');
        return;
      }

      // Find index of the selected row
      const selectedRow = this.selectedSubgroupsTableBoxRow[0];
      let groupIndex;
      let subtotalIndex;
      if (selectedRow['Group Code']) {
        groupIndex = this.previewGroupTableData.findIndex((row) => row['Group Code'] == selectedRow['Group Code']);
        subtotalIndex = this.previewGroupTableData.findIndex((row) => row['Operation'] == selectedRow['Group Code']);
      } else if (this.hasArithmeticOperation(selectedRow['Operation'])) {
        groupIndex = this.previewGroupTableData.findIndex(
          (row) => row['Group Title in Financial Statement'] === selectedRow['Group Title in Financial Statement'],
        );
        subtotalIndex = this.previewGroupTableData.findIndex((row) => row['Operation'] == selectedRow['Operation']);
      } else {
        groupIndex = this.previewGroupTableData.findIndex((row) => row['Group Code'] == selectedRow['Operation']);
        subtotalIndex = this.previewGroupTableData.findIndex((row) => row['Operation'] == selectedRow['Operation']);
      }

      const groupRows = this.previewGroupTableData.slice(groupIndex, subtotalIndex + 1);

      let targetIndex;
      if (groupIndex < this.previewGroupTableData.length - 2) {
        // Swap the selected row with the one below it
        if (
          this.previewGroupTableData[groupIndex]['Record Type'] !=
          this.previewGroupTableData[groupIndex + 1]['Record Type']
        ) {
          if (
            this.previewGroupTableData[groupIndex + 1]['Group Code'] ==
            this.previewGroupTableData[groupIndex + 2]['Operation']
          ) {
            targetIndex = groupIndex + 2;
          } else {
            targetIndex = groupIndex + 1;
          }
        } else {
          targetIndex = groupIndex + 1;
        }
        if (targetIndex < 0) {
          return;
        }

        // Remove the block from the current position
        this.previewGroupTableData.splice(groupIndex, groupRows.length);

        // Insert the block at the new position
        this.previewGroupTableData.splice(targetIndex, 0, ...groupRows);

        // Update selection to reflect the new position
        this.selectedSubgroupsTableBoxRow = [this.previewGroupTableData[targetIndex]];
      } else {
        targetIndex = groupIndex + 1;

        if (targetIndex > this.previewGroupTableData.length - 1) {
          return;
        }

        // Remove the block from the current position
        this.previewGroupTableData.splice(groupIndex, groupRows.length);

        // Insert the block at the new position
        this.previewGroupTableData.splice(targetIndex, 0, ...groupRows);

        // Update selection to reflect the new position
        this.selectedSubgroupsTableBoxRow = [this.previewGroupTableData[targetIndex]];
      }
      this.refreshPreviewTable();
    },

    getFirstPeriodFYear: async function (fyear) {
      this.$fullscreenLoading('Getting Financial Year Start Date');
      const result = await this.$apiCallV2('/FINGLJournal/GetFINGLList', {
        masterfile: 'FYEARSTARTDATE',
        fyear: fyear,
      });
      this.fYearStartDate = new Date(result[0]['fyearstartdate']);
      if (this.fYearStartDate) {
        this.fYearEndDate = new Date(this.fYearStartDate.getFullYear(), this.fYearStartDate.getMonth() + 12, 0);
      }
      this.$fullscreenLoading('');
    },

    getReportTypeData: async function () {
      await this.$nextTick();
      if (this.selectedYear && this.selectedReportType) {
        await this.getFirstPeriodFYear(this.selectedYear['Fyear']);
        if (this.selectedReportType['Report'] == 'Profit & Loss') {
          this.getPNLAnalysisTableData(this.selectedYear['Fyear']);
          this.getPNLGroupUnderList();
        }
        if (this.selectedReportType['Report'] == 'Balance Sheet') {
          this.getBSAnalysisTableData(this.selectedYear['Fyear']);
          this.getBSGroupUnderList();
        }
      }
    },

    getRecordCategoryForPopulate: async function (record) {
      this.showGLAccount = true;
      this.showGroup = false;
      this.showSubtotal = false;
      this.getAccIndList();
      this.getGLCostDeptList();
      this.$fullscreenLoading('Getting Details');
      this.populatePopupEntryFields(await this.getGLAccountDetails(record['GL Account Code']));
      this.$fullscreenLoading();
    },
    groupAddClicked() {
      this.$fullscreenLoading('Loading...');
      const isInvalid = this.checkPopupEntryFields();
      if (!isInvalid) {
        this.refreshPreviewTable();
        this.generateNewGroupPreview();
      }
      this.$fullscreenLoading('');
      return isInvalid;
    },
    generateNewGroupPreview() {
      this.updatePreviewGroupTableData();
      //if preview group table got data, then update the seq number
      if (this.previewGroupTableData.length > 0) {
        let seq = 0;
        this.previewGroupTableData.forEach((f) => {
          seq++;
          f['Seq'] = seq;
        });
      }
    },
    updatePreviewGroupTableData() {
      this.previewGroupTableData = [];
      if (!Array.isArray(this.subgroupsNameList)) {
        this.previewGroupTableData = [];
        return;
      }

      // if after/before not used, then put new records at the end of the array
      if (!this.beforeAfterDropdownValue && !this.existingGroupListValue && this.groupCodeInputValue) {
        this.previewGroupTableData = [
          ...this.subgroupsNameList,
          {
            'Record Type': 'Subgroup',
            'Group Title in Financial Statement': this.groupTitleInputValue,
            'Group Code': this.groupCodeInputValue,
            'Date Updated': this.subgroupNameListFirstDateUpdated,
            TxnType: this.selectedTransactionType ?? this.selectedTransactionType['TxnType'],
            Operation: '',
          },
          {
            'Record Type': 'Operation',
            'Group Title in Financial Statement': this.groupDescription,
            'Group Code': '',
            'Date Updated': this.subgroupNameListFirstDateUpdated,
            Operation: this.groupCodeInputValue,
          },
        ];
        return;
      }

      if (
        !this.beforeAfterDropdownValue &&
        !this.existingGroupListValue &&
        this.subtotalDescription &&
        this.subtotalDescription != ''
      ) {
        this.previewGroupTableData = [
          ...this.subgroupsNameList,
          {
            'Record Type': 'Operation',
            'Group Title in Financial Statement': this.subtotalDescription,
            'Group Code': '',
            'Date Updated': this.subgroupNameListFirstDateUpdated,
            // Operation: this.formatAddSubtractOperation(),
            Operation: this.subtotalOperation,
          },
        ];
        return;
      }

      if (
        !this.beforeAfterDropdownValue ||
        !this.existingGroupListValue ||
        (this.showGroup && !this.groupCodeInputValue)
      ) {
        this.previewGroupTableData = [...this.subgroupsNameList];
        return;
      }

      let index = this.subgroupsNameList.findIndex(
        (group) =>
          group['Group Title in Financial Statement'] === this.existingGroupListValue['Group Title in Financial Statement'], //use group title because is consider unique
      );
      if (index === -1) {
        this.previewGroupTableData = [...this.subgroupsNameList];
        return;
      } else if (this.beforeAfterDropdownValue?.BAdesc === 'After' && this.existingGroupListValue['Record Type'] === 'Subgroup') {
        index = index + 1; //+1 because every subgroup will always have a operation after that record. Is a group so have to use the operation index
      }

      const newList = [...this.subgroupsNameList];

      const newGroup = {
        'Record Type': 'Subgroup',
        'Group Title in Financial Statement': this.groupTitleInputValue,
        'Group Code': this.groupCodeInputValue,
        'Date Updated': this.subgroupNameListFirstDateUpdated,
        TxnType: this.showGroup ? this.selectedTransactionType['TxnType'] : '',
        Operation: '',
      };

      const newOperation = {
        'Record Type': 'Operation',
        'Group Title in Financial Statement': this.showGroup ? this.groupDescription : this.subtotalDescription,
        'Group Code': '',
        'Date Updated': this.subgroupNameListFirstDateUpdated,
        Operation: this.showGroup ? this.groupCodeInputValue : this.subtotalOperation,
      };

      if (this.beforeAfterDropdownValue?.BAdesc === 'Before') {
        if (this.showGroup) {
          newList.splice(index, 0, newGroup);
          index = index + 1; //for the next splice below to add after the first splice
        }
        newList.splice(index, 0, newOperation);
      } else {
        if (this.showGroup) {
          index = index + 1; //for the splice below, add after the index
          newList.splice(index, 0, newGroup);
        }
        index = index + 1; //for the next splice below, add after the first splice
        newList.splice(index, 0, newOperation);
      }
      this.previewGroupTableData = newList;
    },
    async editPreviewGroupClick(_btn, data) {
      //check records is group or operation
      let isGroup = data['Group Code'] && data['Group Code'] != '';
      if (!isGroup) {
        //check again with operation column if no symbol means is a group
        if (data['Operation'] && data['Operation'] != '' && !this.hasArithmeticOperation(data['Operation'])) {
          isGroup = true;
          data['Group Code'] = data['Operation'];
        }
      }

      this.addEditCancelClick(); //reset fields
      if (isGroup) {
        this.showGroup = true;
        this.selectedCategory = this.computedCategoryList.getSelected('group', 'key');
      } else {
        this.showSubtotal = true;
        this.selectedCategory = this.computedCategoryList.getSelected('subtotal', 'key');
      }
      this.editGroup = true;
      await this.populateGroupOperations(data);
      this.showGroupNewEdit = true;
    },
    populateGroupOperations(details) {
      if (this.showGroup) {
        let groupTitle = '';
        let groupDesc = '';
        let txntype = '';
        if (details['Record Type'] == 'Subgroup') {
          groupTitle = details['Group Title in Financial Statement'];
          groupDesc = this.previewGroupTableData.find((f) => f['Operation'] == details['Group Code'])[
            'Group Title in Financial Statement'
          ]; //find the record with operation same as the group code
          txntype = details['TxnType'];
        } else {
          const findGroup = this.previewGroupTableData.find((f) => f['Group Code'] == details['Group Code']);
          groupTitle = findGroup['Group Title in Financial Statement']; //find the record with group code same as details group code
          groupDesc = details['Group Title in Financial Statement'];
          txntype = findGroup['TxnType'];
        }
        this.groupCodeInputValue = details['Group Code'];
        this.groupDescription = groupDesc; //find the records with operation same as the group code
        this.groupTitleInputValue = groupTitle;
        this.selectedTransactionType =
          txntype ? this.populateObject(this.transactionTypeList, 'TxnType', txntype) : null;
      } else if (this.showSubtotal) {
        this.subtotalSeq = details['Seq'];
        this.subtotalDescription = details['Group Title in Financial Statement'];
        this.subtotalOperation = details['Operation'];
      }
    },
    addEditCancelClick() {
      this.resetPopupEntryFields();
      this.clearPopupEntryMessages();
      this.selectedCategory = null;
      this.showGroup = false;
      this.showSubtotal = false;
      this.showGroupNewEdit = false;
      this.editGroup = false;
    },
    addGroupClick() {
      const isInvalid = this.groupAddClicked();
      if (isInvalid) {
        return;
      }

      this.subgroupsNameList = [];
      this.subgroupsNameList = JSON.parse(JSON.stringify(this.previewGroupTableData));

      //add into array as new action if is group,
      if (this.showGroup) {
        this.groupChangesArray.push({
          groupcode: this.groupCodeInputValue,
          groupdescription: this.groupDescription,
          txntype: this.selectedTransactionType['TxnType'],
          action: 'N',
        });
      }

      this.resetPopupEntryFields();
      this.addEditCancelClick();
    },
    editGroupClick() {
      const isInvalid = this.checkPopupEntryFields();
      if (isInvalid) {
        return;
      }

      if (this.showGroup) {
        //group edit
        const findGroup = this.subgroupsNameList.find((f) => f['Group Code'] == this.groupCodeInputValue);
        findGroup['Group Title in Financial Statement'] = this.groupTitleInputValue;
        findGroup['TxnType'] = this.selectedTransactionType['TxnType'];
        const findOperation = this.subgroupsNameList.find((f) => f['Operation'] == this.groupCodeInputValue);
        findOperation['Group Title in Financial Statement'] = this.groupDescription;

        //only showgroup need to add into groupChangesArray
        const findObj = this.groupChangesArray.find((f) => f['groupcode'] == this.groupCodeInputValue);
        if (findObj) {
          //if already have group code in the array, then just edit the object
          findObj['groupdescription'] = this.groupDescription;
          findObj['txntype'] = this.selectedTransactionType['TxnType'];
        } else {
          //add into array as edit action
          this.groupChangesArray.push({
            groupcode: this.groupCodeInputValue,
            groupdescription: this.groupDescription,
            txntype: this.selectedTransactionType['TxnType'],
            action: 'E',
          });
        }
      } else {
        //subtotal edit
        const findOperation = this.subgroupsNameList.find((f) => f['Operation'] == this.subtotalOperation);
        findOperation['Group Title in Financial Statement'] = this.subtotalDescription;
        findOperation['Operation'] = this.subtotalOperation;
      }

      this.resetPopupEntryFields(); //reset first because in generateNewGroupPreview will use the current field value to add to the array causing duplicate
      this.refreshPreviewTable();
      this.generateNewGroupPreview();
      this.addEditCancelClick();
    },
    async deleteGroupClick() {
      let deleteGroupCode = '';
      let deleteGroupDescription = '';
      let deleteGroupTxnType = '';
      if (this.selectedSubgroupsTableBoxRow.length == 0) {
        this.$showAlert('error', 'No Record Selected', 'Please select a subgroup or subtotal to delete');
        return;
      }

      //find the group code, leave as empty if is subtotal
      if (this.selectedSubgroupsTableBoxRow[0]['Record Type'].toUpperCase() == 'SUBGROUP') {
        deleteGroupCode = this.selectedSubgroupsTableBoxRow[0]['Group Code'];
      } else {
        deleteGroupCode =
          this.hasArithmeticOperation(this.selectedSubgroupsTableBoxRow[0]['Operation']) ? '' : (
            this.selectedSubgroupsTableBoxRow[0]['Operation']
          );
      }

      //check if there is any operation subtotal have the same group code
      if (
        deleteGroupCode != '' &&
        this.previewGroupTableData.some(
          (f) => this.hasArithmeticOperation(f['Operation']) && f['Operation'].includes(deleteGroupCode),
        )
      ) {
        this.$showAlert('error', 'Cannot Delete Subgroup', `Subgroup still in use for subtotals`);
        return;
      }

      const response = await this.$showAlert(
        'warning',
        `Delete ${deleteGroupCode != '' ? 'Subgroup' : 'Operation'}`,
        `Are you sure you want to remove ${deleteGroupCode != '' ? 'Subgroup' : 'Operation'}(${deleteGroupCode != '' ? deleteGroupCode : this.selectedSubgroupsTableBoxRow[0]['Operation']})?`,
        [
          { text: 'Delete', color: 'red', textColor: 'white' },
          { text: 'Cancel', color: 'lightgray', textColor: 'black' },
        ],
      );
      if (response == 'Cancel') {
        return;
      }

      //delete the records from the original table
      if (deleteGroupCode != '') {
        //delete group
        const findGroupIndex = this.subgroupsNameList.findIndex((f) => f['Group Code'] == deleteGroupCode);
        if (findGroupIndex != -1) {
          deleteGroupDescription = this.subgroupsNameList[findGroupIndex]['Group Title in Financial Statement'];
          deleteGroupTxnType = this.subgroupsNameList[findGroupIndex]['TxnType'];
          this.subgroupsNameList.splice(findGroupIndex, 1);
        }
        const findOperationIndex = this.subgroupsNameList.findIndex((f) => f['Operation'] == deleteGroupCode);
        if (findOperationIndex != -1) {
          this.subgroupsNameList.splice(findOperationIndex, 1);
        }
      } else {
        //delete subtotal
        const findSubtotalIndex = this.subgroupsNameList.findIndex(
          (f) => f['Operation'] == this.selectedSubgroupsTableBoxRow[0]['Operation'],
        );
        if (findSubtotalIndex != -1) {
          this.subgroupsNameList.splice(findSubtotalIndex, 1);
        }
      }

      const findIndex = this.groupChangesArray.findIndex(
        (f) => f['action'] == 'N' && f['groupcode'] == deleteGroupCode,
      );
      if (findIndex != -1) {
        //if find in arrray for new action then just remove from array
        this.groupChangesArray.splice(findIndex, 1);
      } else if (deleteGroupCode != '') {
        //add into array as edit action if not found from array
        this.groupChangesArray.push({
          groupcode: deleteGroupCode,
          groupdescription: deleteGroupDescription,
          txntype: deleteGroupTxnType,
          action: 'D',
        });
      }

      //refresh the table
      this.refreshPreviewTable();
      this.generateNewGroupPreview();
    },
    hasArithmeticOperation(value) {
      //to check if value have + or - sign
      return /[+\\-]/.test(value);
    },
  },
  mounted() {
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'GL Master') {
        x.data.forEach((k) => {
          // Key in menu same as control value from API
          const selectedMenu = this.menu.find((m) => m['key'] == k['Control']);
          if (selectedMenu) {
            if (k.Properties === 'Visible' || k.Properties === 'Enabled') {
              selectedMenu['visible'] = selectedMenu['enabled'] = k['PropertiesValue'] === 'True';
            }
          }
        });
      }
    });

    const mountedPromises = [];
    let promisesFulfilled = 0;

    mountedPromises.push(
      this.getGLAccountList().then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      })
    ),

    mountedPromises.push(
      this.getYearListMaster().then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.getReportTypeListMaster().then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.getTxnType().then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'GETGLMASTERTAXCODE' }).then((result) => {
        this.defaultTaxCodeList = result.map((item) => ({
          ...item,
          taxCodeText: `${item['Tax Code']} - ${item['Tax Code Description']}`,
        }));
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );
    mountedPromises.push(
      this.getChargeCodeListMaster().then(() => {
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CHARGESCODECLASS' }).then((result) => {
        this.GLMasterClassListMaster = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'MSIC' }).then((result) => {
        this.MSICData = result;
        this.$topLoading(++promisesFulfilled / mountedPromises.length);
      }),
    );

    Promise.allSettled(mountedPromises).then((_promiseOutcomes) => {
      this.menu = this.menu.map((menu) => ({
        ...menu,
        enabled: true,
      }));
    });
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.gl-master-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;

  > .table-box-container {
    width: 100%;
    margin-left: 5px;
    padding: 5px;
    background-color: white;
    border-radius: 5px;
    display: flex;
    flex-direction: column;
    width: 100%;
    overflow-x: scroll;
  }

  .dropdown-container {
    width: 50%;
    display: flex;
    flex-direction: row;
  }

  .input-left-container {
    display: flex;
    flex-direction: column;
    width: 98%;
    flex-grow: 1;
    max-height: 100%;
  }

  .input-center-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 98%;
    flex-grow: 1;
    padding: 0 1em;
    max-height: 100%;
  }

  .input-right-container {
    display: flex;
    flex-direction: column;
    width: 98%;
    flex-grow: 1;
    max-height: 100%;
  }

  .input-container {
    display: flex;
    justify-content: space-between;
    flex-direction: row;
    width: 100%;
    max-height: 100%;
  }
  .ap-ar-table-box-container {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex-grow: 1;
    min-height: 28vh;
    max-height: 28vh;
  }
  .add-delete-buttons {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }
  .ap-ar-tablebox {
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: 30vh;
    min-height: 30vh;
    max-height: 30vh;
    overflow-y: auto;
    overflow-x: scroll;
  }
  .charge-code-input-selection {
    max-height: 70vh;
  }
  .subtotal-container {
    display: flex;
    flex-direction: column;
    width: 98%;
    flex-grow: 1;
    max-height: 65vh;
  }
  .subtotal-center-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: 98%;
    flex-grow: 1;
    padding: 0 1em;
    max-height: 65vh;
  }
  .reorder-buttons {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 1em;
  }
}
</style>
